import * as XLSX from "xlsx";

const normalizeHeader = (header) =>
  String(header ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getCol = (row, headerMap, ...keys) => {
  for (const k of keys) {
    const originalKey = headerMap[k];
    if (originalKey && row[originalKey] !== undefined && row[originalKey] !== "") {
      return String(row[originalKey]).trim();
    }
  }
  return "";
};

const parsePropertySheet = (jsonRows, headerMap, startRow) => {
  const rows = [];
  jsonRows.forEach((row, idx) => {
    const finalUrl = getCol(row, headerMap, "final_url", "finalurl", "final_url");
    const businessName = getCol(row, headerMap, "business_name", "businessname", "business_name_(max_characters_25)", "business_name_(max_characters_25)");
    if (finalUrl) rows.push({ _rowIndex: startRow + idx, assetType: "final_url", text: finalUrl });
    if (businessName) rows.push({ _rowIndex: startRow + idx, assetType: "business_name", text: businessName });
  });
  return rows;
};

const parseSingleFieldSheet = (jsonRows, headerMap, assetType, columnKeys, startRow) =>
  jsonRows.map((row, idx) => ({
    _rowIndex: startRow + idx,
    assetType,
    text: getCol(row, headerMap, ...columnKeys),
  }));

const parseSitelinkSheet = (jsonRows, headerMap, startRow) =>
  jsonRows.map((row, idx) => ({
    _rowIndex: startRow + idx,
    assetType: "sitelink",
    sitelinkText: getCol(row, headerMap, "sitelink_text", "sitelinktext", "sitelink_text_(max_characters_25)"),
    description1: getCol(row, headerMap, "sitelink_description_1", "sitelink_description1", "sitelinkdescription1", "sitelink_description_1_(max_characters_25)"),
    description2: getCol(row, headerMap, "sitelink_description_2", "sitelink_description2", "sitelinkdescription2", "sitelink_description_2_(max_characters_25)"),
    finalURL: getCol(row, headerMap, "sitelink_final_url", "sitelinkfinalurl", "sitelink_final_url_(max_characters_25)"),
  }));

const parseCallsSheet = (jsonRows, headerMap, startRow) =>
  jsonRows.map((row, idx) => ({
    _rowIndex: startRow + idx,
    assetType: "phone_call",
    phoneNumber: getCol(row, headerMap, "phone_number", "phonenumber"),
    countryCode: getCol(row, headerMap, "country_code", "countrycode"),
  }));

const parseDisplayPathSheet = (jsonRows, headerMap, startRow) => {
  const rows = [];
  jsonRows.forEach((row, idx) => {
    const path1 = getCol(row, headerMap, "display_path_1", "displaypath1", "display_path1", "display_path_1_(max_character_15)");
    const path2 = getCol(row, headerMap, "display_path_2", "displaypath2", "display_path2", "display_path_2_(max_character_15)");
    if (path1) rows.push({ _rowIndex: startRow + idx, assetType: "display_path", text: path1, pathId: "path1" });
    if (path2) rows.push({ _rowIndex: startRow + idx, assetType: "display_path", text: path2, pathId: "path2" });
  });
  return rows;
};

/**
 * Parse a multi-sheet XLSX workbook
 */
const parseMultiSheet = (workbook) => {
  const allRows = [];
  workbook.SheetNames.forEach((sheetName) => {
    const normalizedName = sheetName.trim().toLowerCase();
    const sheet = workbook.Sheets[sheetName];
    const jsonRows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
    if (!jsonRows.length) return;

    const headerMap = {};
    Object.keys(jsonRows[0]).forEach((h) => { headerMap[normalizeHeader(h)] = h; });
    const startRow = 2;

    if (normalizedName === "property") allRows.push(...parsePropertySheet(jsonRows, headerMap, startRow));
    else if (normalizedName === "headlines") allRows.push(...parseSingleFieldSheet(jsonRows, headerMap, "headline", ["headline", "headline_(max_characters_30)", "text"], startRow));
    else if (normalizedName === "descriptions") allRows.push(...parseSingleFieldSheet(jsonRows, headerMap, "description", ["description", "description_(max_characters_90)", "text"], startRow));
    else if (normalizedName === "site link extensions") allRows.push(...parseSitelinkSheet(jsonRows, headerMap, startRow));
    else if (normalizedName === "callout extensions") allRows.push(...parseSingleFieldSheet(jsonRows, headerMap, "callout", ["callout", "callouts_(max_characters_25)", "text"], startRow));
    else if (normalizedName === "structured snippets") allRows.push(...parseSingleFieldSheet(jsonRows, headerMap, "structured_snippet", ["snippet", "snippet_(max_character_25)", "text"], startRow));
    else if (normalizedName === "calls") allRows.push(...parseCallsSheet(jsonRows, headerMap, startRow));
    else if (normalizedName === "display path") allRows.push(...parseDisplayPathSheet(jsonRows, headerMap, startRow));
  });
  return allRows;
};

/**
 * Parse a single-sheet file (CSV with "section" column or legacy "asset_type" column)
 */
const parseSingleSheet = (workbook) => {
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  const jsonRows = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });
  if (!jsonRows.length) return [];

  const headerMap = {};
  Object.keys(jsonRows[0]).forEach((h) => { headerMap[normalizeHeader(h)] = h; });

  const sectionKey = headerMap["section"];
  const assetTypeKey = headerMap["asset_type"];
  const isNewFormat = !!sectionKey;

  return jsonRows.map((row, idx) => {
    const startRow = idx + 2;

    if (isNewFormat) {
      const sectionName = String(row[sectionKey] ?? "").trim().toLowerCase();
      if (sectionName === "property") {
        const finalUrl = getCol(row, headerMap, "final_url", "finalurl", "final_url");
        const businessName = getCol(row, headerMap, "business_name", "businessname", "business_name_(max_characters_25)", "business_name_(max_characters_25)");
        if (finalUrl) return { _rowIndex: startRow, assetType: "final_url", text: finalUrl, _businessName: businessName };
        if (businessName) return { _rowIndex: startRow, assetType: "business_name", text: businessName };
        return null;
      }
      if (sectionName === "headlines") return { _rowIndex: startRow, assetType: "headline", text: getCol(row, headerMap, "headline", "headline_(max_characters_30)", "text") };
      if (sectionName === "descriptions") return { _rowIndex: startRow, assetType: "description", text: getCol(row, headerMap, "description", "description_(max_characters_90)", "text") };
      if (sectionName === "site link extensions") {
        return {
          _rowIndex: startRow, assetType: "sitelink",
          sitelinkText: getCol(row, headerMap, "sitelink_text", "sitelinktext", "sitelink_text_(max_characters_25)"),
          description1: getCol(row, headerMap, "sitelink_description_1", "sitelink_description1", "sitelink_description_1_(max_characters_25)"),
          description2: getCol(row, headerMap, "sitelink_description_2", "sitelink_description2", "sitelink_description_2_(max_characters_25)"),
          finalURL: getCol(row, headerMap, "sitelink_final_url", "sitelinkfinalurl", "sitelink_final_url_(max_characters_25)"),
        };
      }
      if (sectionName === "callout extensions") return { _rowIndex: startRow, assetType: "callout", text: getCol(row, headerMap, "callout", "callouts_(max_characters_25)", "text") };
      if (sectionName === "structured snippets") return { _rowIndex: startRow, assetType: "structured_snippet", text: getCol(row, headerMap, "snippet", "snippet_(max_character_25)", "text") };
      if (sectionName === "calls") {
        return { _rowIndex: startRow, assetType: "phone_call", phoneNumber: getCol(row, headerMap, "phone_number", "phonenumber"), countryCode: getCol(row, headerMap, "country_code", "countrycode") };
      }
      if (sectionName === "display path") {
        const path1 = getCol(row, headerMap, "display_path_1", "displaypath1", "display_path_1_(max_character_15)");
        const path2 = getCol(row, headerMap, "display_path_2", "displaypath2", "display_path_2_(max_character_15)");
        const rows = [];
        if (path1) rows.push({ _rowIndex: startRow, assetType: "display_path", text: path1, pathId: "path1" });
        if (path2) rows.push({ _rowIndex: startRow, assetType: "display_path", text: path2, pathId: "path2" });
        return rows;
      }
      return null;
    }

    // Legacy format: asset_type column
    if (!assetTypeKey) return null;
    const rawType = normalizeHeader(row[assetTypeKey]);
    const assetType = rawType === "structured_snippet" ? "structured_snippet" : rawType;
    const mapped = { _rowIndex: startRow, assetType };

    if (assetType === "sitelink") {
      mapped.sitelinkText = getCol(row, headerMap, "sitelink_text", "sitelinktext", "sitelink_text_(max_characters_25)");
      mapped.description1 = getCol(row, headerMap, "sitelink_description1", "sitelinkdescription1", "sitelink_description_1", "sitelink_description_1_(max_characters_25)");
      mapped.description2 = getCol(row, headerMap, "sitelink_description2", "sitelinkdescription2", "sitelink_description_2", "sitelink_description_2_(max_characters_25)");
      mapped.finalURL = getCol(row, headerMap, "sitelink_final_url", "sitelinkfinalurl", "sitelink_final_url_(max_characters_25)");
    } else if (assetType === "phone_call") {
      mapped.phoneNumber = getCol(row, headerMap, "text", "phone_number", "phonenumber");
      mapped.countryCode = getCol(row, headerMap, "sitelink_text", "country_code", "countrycode");
    } else if (assetType === "display_path") {
      const p1 = getCol(row, headerMap, "display_path_1", "displaypath1", "display_path_1_(max_character_15)");
      const p2 = getCol(row, headerMap, "display_path_2", "displaypath2", "display_path_2_(max_character_15)");
      if (p1 || p2) {
        const rows = [];
        if (p1) rows.push({ ...mapped, text: p1, pathId: "path1" });
        if (p2) rows.push({ ...mapped, text: p2, pathId: "path2" });
        return rows;
      }
      mapped.text = getCol(row, headerMap, "text", "content");
    } else {
      mapped.text = getCol(row, headerMap, "text", "content");
    }
    return mapped;
  });
};

/**
 * Read and parse a File (CSV or XLSX).
 * Supports multi-sheet XLSX, single-sheet CSV with "section" column,
 * and legacy single-sheet with "asset_type" column.
 */
export const parseUploadFile = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        let parsedRows;

        if (workbook.SheetNames.length > 1) {
          parsedRows = parseMultiSheet(workbook);
        } else {
          const rawParsed = parseSingleSheet(workbook);
          parsedRows = rawParsed.flat().filter(Boolean);
          const extraRows = [];
          parsedRows.forEach((row) => {
            if (row._businessName) {
              extraRows.push({ _rowIndex: row._rowIndex, assetType: "business_name", text: row._businessName });
              delete row._businessName;
            }
          });
          parsedRows.push(...extraRows);
        }

        if (!parsedRows.length) {
          resolve({ rows: [], errors: ["The uploaded file contains no data rows."] });
          return;
        }
        resolve({ rows: parsedRows, errors: [] });
      } catch (err) {
        resolve({ rows: [], errors: [`Failed to parse file: ${err.message}`] });
      }
    };
    reader.onerror = () => resolve({ rows: [], errors: ["Failed to read the file."] });
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Group parsed rows by asset type
 */
export const groupByAssetType = (rows) => {
  const grouped = {
    final_url: [], business_name: [], headline: [], description: [],
    sitelink: [], callout: [], structured_snippet: [],
    display_path: [], phone_call: [],
  };
  rows.forEach((row) => { if (grouped[row.assetType]) grouped[row.assetType].push(row); });
  return grouped;
};